import {
    ConversationSelectedEvent,
    SendMessageEvent,
    InboxOptions,
    Message,
    UnreadConversation,
} from 'talkjs/types/talk.types';
import { CommunicationData } from '~/models';
import CommunicationService from '~/services/CommunicationService';
import AnalyticService from '~/services/AnalyticService';
import Talk from 'talkjs';
import { Inbox, Session } from 'talkjs/all';
import { User } from '~/services/types/identity';
import store from '~/store';

export default class ChatService {
    private talkSession: Session | null = null;
    private talkJsUserIdToUserIdMap: { [key: string]: string } = {};

    private myCommunicationData = async (): Promise<CommunicationData | null> =>
        await CommunicationService.getMe();

    async init() {
        await this.refreshSession();
        const myCommunicationData = await this.myCommunicationData();
        if (myCommunicationData?.isDummyUser && this.talkSession) {
            this.talkSession.onMessage(this.onDummyUserMessageReceived);
        } else {
            this.talkSession?.onMessage(this.onMessageReceived);
            this.talkSession?.unreads.onChange(this.onUnreadConversationsChanged);
        }
    }

    async tryCreateInbox(
        theme: string,
        externalOnConversationSelected: (event: ConversationSelectedEvent) => void
    ): Promise<Inbox | null> {
        const myCommunicationData = await CommunicationService.getMe();
        if (!myCommunicationData?.isDummyUser && this.talkSession) {
            const inbox = this.talkSession.createInbox({
                theme,
            } as InboxOptions);
            inbox.onConversationSelected(externalOnConversationSelected);
            inbox.onConversationSelected(event => this.onConversationSelected(event));
            inbox.onSendMessage(event => this.onSendMessage(event));
            return inbox;
        }
        return null;
    }

    private myTalkJsUser = async (): Promise<Talk.User | null> => {
        const myCommunicationData = await this.myCommunicationData();
        if (myCommunicationData) {
            return new Talk.User(myCommunicationData.id);
        }
        return null;
    };

    private onConversationSelected(event: ConversationSelectedEvent) {
        if (event.conversation?.custom['context_type'] !== 'company') {
            return;
        }
        if (!event.others || event.others.length === 0) {
            console.error('TalkJS conversation has no other participants');
            return;
        }
        event.others.forEach(other => {
            if (other.custom['userId']) {
                this.talkJsUserIdToUserIdMap[other.id] = other.custom['userId'];
                console.debug(
                    'TalkJS User' + other.id + ' is mapped to user ' + other.custom['userId']
                );
            } else console.error('TalkJS User' + other.id + ' has no custom field "userId"');
        });
    }

    private onSendMessage(event: SendMessageEvent) {
        if (event.conversation.custom['context_type'] !== 'company') {
            return;
        }

        let participants = Object.keys(event.conversation.participants);
        if (participants.length !== 2) {
            console.error('TalkJS conversation has wrong count of participants');
            return;
        }

        let conversation_id = event.conversation.id;

        let sender_talkjs_user_id = event.me.id;
        let sender_role = event.conversation.custom[sender_talkjs_user_id];
        let sender_user_id = event.me.custom['userId'];

        let receipient_talkjs_user_id =
            participants[0] !== event.me.id ? participants[0] : participants[1];
        let recepient_role = event.conversation.custom[receipient_talkjs_user_id];
        let recepient_user_id = this.talkJsUserIdToUserIdMap[receipient_talkjs_user_id];

        let context_id = event.conversation.custom['context_id'];
        let context_type = event.conversation.custom['context_type'];
        let context_title = event.conversation.custom['context_name'];

        if (
            !conversation_id ||
            !sender_talkjs_user_id ||
            !sender_role ||
            !sender_user_id ||
            !receipient_talkjs_user_id ||
            !recepient_role ||
            !recepient_user_id ||
            !context_id ||
            !context_type ||
            !context_title
        ) {
            console.error('TalkJS conversation has missing fields: ' + JSON.stringify(event));
            return;
        }

        // console.info('conversation_id: ' + conversation_id);
        // console.info('sender_talkjs_user_id: ' + sender_talkjs_user_id);
        // console.info('sender_role: ' + sender_role);
        // console.info('sender_user_id: ' + sender_user_id);
        // console.info('receipient_talkjs_user_id: ' + receipient_talkjs_user_id);
        // console.info('recepient_role: ' + recepient_role);
        // console.info('recepient_user_id: ' + recepient_user_id);
        // console.info('context_id: ' + context_id);
        // console.info('context_type: ' + context_type);
        // console.info('context_title: ' + context_title);

        AnalyticService.messageSentEvent(
            conversation_id,
            sender_talkjs_user_id.toString(),
            sender_role,
            sender_user_id,
            receipient_talkjs_user_id,
            recepient_role,
            recepient_user_id,
            context_id,
            context_type,
            context_title
        );
    }

    private onMessageReceived(message: Message) {
        store.dispatch('talkSessionModule/updateCurrentConversationId', message.conversation.id);
    }

    private async onDummyUserMessageReceived(message: Message) {
        const myIdentityResult: User = await store.getters['profileModule/getUser']();
        const myCommunicationData = await this.myCommunicationData();
        if (!myCommunicationData?.isDummyUser) {
            return;
        } else {
            if (
                message.custom['refreshDummy'] &&
                message.custom['userId'] === myIdentityResult.id
            ) {
                await this.refreshSession();
                this.talkSession?.unreads.onChange(this.onUnreadConversationsChanged);
            }
        }
    }

    private async refreshSession() {
        const myCommunicationData = await this.myCommunicationData();
        if (!myCommunicationData) {
            console.error('[Chat]: User communication data is missing');
            return;
        }
        const myTalkJsUser = await this.myTalkJsUser();
        if (!myTalkJsUser) {
            console.error('[Chat]: TalkJsUser is no initialized');
            return;
        }
        if (this.talkSession) {
            this.talkSession?.destroy();
        }
        this.talkSession = new Talk.Session({
            appId: process.env.VUE_APP_TALKJS_APPID,
            me: myTalkJsUser,
            signature: myCommunicationData.signature,
        });
        store.dispatch('talkSessionModule/updateMyTalkJsUser', myTalkJsUser);
    }

    private onUnreadConversationsChanged(unreadConversations: UnreadConversation[]) {
        if (unreadConversations.length == 0) {
            store.dispatch('talkSessionModule/updateUnreadMessagesCount', null);
        } else {
            const realUnreadMessageCount = unreadConversations.reduce((total, conversation) => {
                return total + conversation.unreadMessageCount;
            }, 0);
            store.dispatch('talkSessionModule/updateUnreadMessagesCount', realUnreadMessageCount);
        }
    }
}
